import type { 
  Container, 
  Column, 
  Element, 
  FormComponent, 
  DesignProperties,
  FormStructure 
} from '../types/form-builder';

export function generateUID(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function createContainer(title?: string): Container {
  return {
    uid: generateUID(),
    type: 'container',
    title: title || '',
    columns: [createColumn(12)],
    design: {},
    logic: [],
    collapsible: false,
    collapsed: false,
  };
}

export function createColumn(width: number = 6): Column {
  return {
    uid: generateUID(),
    type: 'column',
    width,
    elements: [],
    design: {},
    logic: [],
  };
}

export function createElement(elementType: Element['elementType'], fieldId?: string): Element {
  const uid = generateUID();
  return {
    uid,
    type: 'element',
    elementType,
    fieldId: fieldId || `field_${uid}`,
    label: getDefaultLabel(elementType),
    placeholder: '',
    helpText: '',
    design: {},
    logic: [],
    validation: [],
    required: false,
    disabled: false,
    readonly: false,
  };
}

function getDefaultLabel(elementType: Element['elementType']): string {
  const labels: Record<string, string> = {
    text: 'Text Field',
    textarea: 'Text Area',
    number: 'Number',
    email: 'Email',
    phone: 'Phone',
    date: 'Date',
    time: 'Time',
    datetime: 'Date & Time',
    select: 'Dropdown',
    multiselect: 'Multi-Select',
    radio: 'Radio Buttons',
    checkbox: 'Checkbox',
    checkboxGroup: 'Checkbox Group',
    file: 'File Upload',
    image: 'Image',
    signature: 'Signature',
    rating: 'Rating',
    slider: 'Slider',
    toggle: 'Toggle',
    heading: 'Heading',
    paragraph: 'Paragraph',
    divider: 'Divider',
    spacer: 'Spacer',
    button: 'Button',
    hidden: 'Hidden Field',
  };
  return labels[elementType] || 'Field';
}

export function applyInlineStyles(design?: DesignProperties): Record<string, string> {
  if (!design) return {};

  const styles: Record<string, string> = {};

  if (design.backgroundColor) styles.backgroundColor = design.backgroundColor;
  if (design.textColor) styles.color = design.textColor;
  if (design.borderColor) styles.borderColor = design.borderColor;
  if (design.borderWidth !== undefined) styles.borderWidth = `${design.borderWidth}px`;
  if (design.borderRadius !== undefined) styles.borderRadius = `${design.borderRadius}px`;
  if (design.padding) styles.padding = design.padding;
  if (design.margin) styles.margin = design.margin;
  if (design.fontSize) styles.fontSize = design.fontSize;
  if (design.fontWeight) styles.fontWeight = design.fontWeight;
  if (design.textAlign) styles.textAlign = design.textAlign;
  if (design.width) styles.width = design.width;
  if (design.minHeight) styles.minHeight = design.minHeight;

  if (design.borderColor || design.borderWidth) {
    styles.borderStyle = 'solid';
  }

  return styles;
}

export interface FindResult {
  component: FormComponent | null;
  parent: Container | Column | null;
  index: number;
}

export function findComponentByUID(
  containers: Container[],
  uid: string
): FindResult {
  for (const container of containers) {
    if (container.uid === uid) {
      return { component: container, parent: null, index: containers.indexOf(container) };
    }

    for (const column of container.columns) {
      if (column.uid === uid) {
        return { component: column, parent: container, index: container.columns.indexOf(column) };
      }

      for (const element of column.elements) {
        if (element.uid === uid) {
          return { component: element, parent: column, index: column.elements.indexOf(element) };
        }
      }
    }
  }

  return { component: null, parent: null, index: -1 };
}

export function cloneComponent<T extends FormComponent>(component: T): T {
  const cloned = JSON.parse(JSON.stringify(component)) as T;
  regenerateUIDs(cloned);
  return cloned;
}

function regenerateUIDs(component: FormComponent): void {
  component.uid = generateUID();

  if (component.type === 'container') {
    for (const column of (component as Container).columns) {
      regenerateUIDs(column);
    }
  } else if (component.type === 'column') {
    for (const element of (component as Column).elements) {
      regenerateUIDs(element);
    }
  }
}

export function getAllFieldIds(structure: FormStructure): string[] {
  const fieldIds: string[] = [];

  for (const container of structure.containers) {
    for (const column of container.columns) {
      for (const element of column.elements) {
        if (element.fieldId) {
          fieldIds.push(element.fieldId);
        }
      }
    }
  }

  return fieldIds;
}

export function validateFieldId(fieldId: string, existingIds: string[], currentId?: string): boolean {
  if (!fieldId || fieldId.trim() === '') return false;
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(fieldId)) return false;
  
  const filteredIds = currentId 
    ? existingIds.filter(id => id !== currentId)
    : existingIds;
    
  return !filteredIds.includes(fieldId);
}

export function createEmptyFormStructure(): FormStructure {
  return {
    version: '1.0.0',
    containers: [],
    metadata: {
      title: 'Untitled Form',
      createdAt: new Date().toISOString(),
    },
  };
}
